import {
	advanceRun,
	type InputEvent,
	queueInput,
	type RealtimeGame,
	type RealtimeState,
	type RunProgress,
	runFinished,
	startRun,
} from "@playpal/core";
import { useCallback, useEffect, useRef, useState } from "react";

export interface RealtimeRun<S extends RealtimeState, I> {
	state: S;
	/** Crashed, as opposed to having simply run out of clock. */
	over: boolean;
	/** Nothing more will happen: crashed, or the clock ran out. */
	finished: boolean;
	/** Records a press, to be applied on one of the next few steps. */
	press: (input: I) => void;
	/**
	 * The run as it stands this frame, rather than as it was last drawn.
	 *
	 * A function because it lives in a ref and is a frame ahead of `state`,
	 * which is exactly what a caller wants for the two things that need it: a
	 * key press, which has to know about turns already queued but not yet
	 * walked, and giving up, which has to send the presses made so far.
	 */
	peek: () => RunProgress<S, I>;
}

/**
 * Runs one arcade game in this browser and hands the log over when it ends.
 *
 * Everything that decides the outcome lives in `packages/core` — this only
 * supplies a clock and somewhere to put the result. That split is the whole
 * design: the server replays the same core loop over the log it receives, so
 * any cleverness added here would be cleverness the server does not know
 * about, which is another way of saying a child's score would stop matching
 * the game they just played.
 *
 * The animation frame is the clock rather than an interval, because an interval
 * at sixty steps a second drifts and stacks up, while frames stop arriving when
 * nobody is looking — which is exactly when a run should not be advancing.
 */
export function useRealtimeRun<S extends RealtimeState, I>(
	game: RealtimeGame<S, I>,
	seed: number,
	active: boolean,
	onFinish: (log: readonly InputEvent<I>[], state: S) => void,
): RealtimeRun<S, I> {
	/*
	 * Populated lazily. `useRef(startRun(...))` reads as run-once but is not:
	 * the argument is evaluated on every render — a full game-state allocation
	 * built and thrown away up to sixty times a second — with only the first
	 * ever kept. The null start plus the guard below is the run-once version.
	 */
	const progressRef = useRef<RunProgress<S, I> | null>(null);
	if (progressRef.current === null) progressRef.current = startRun(game, seed);
	const [state, setState] = useState<S>(progressRef.current.state);

	// Held in a ref so a caller that rebuilds the callback every render — which
	// is most of them — does not restart the run underneath the player.
	const finishRef = useRef(onFinish);
	finishRef.current = onFinish;
	const activeRef = useRef(active);
	activeRef.current = active;

	useEffect(() => {
		const fresh = startRun(game, seed);
		progressRef.current = fresh;
		setState(fresh.state);
	}, [game, seed]);

	useEffect(() => {
		if (!active) return;

		let frame = 0;
		let last = performance.now();

		const tick = (now: number) => {
			// Always populated by the render that scheduled this frame; the null
			// exists only so the ref could start lazily.
			const current = progressRef.current;
			if (current === null) return;
			const previous = current.state;
			const next = advanceRun(game, current, now - last);
			last = now;
			progressRef.current = next;

			// Only when a step actually happened. Sixty frames into an
			// eight-step-a-second game means seven frames in eight change nothing,
			// and re-drawing the board for those is work nobody can see.
			if (next.state !== previous) setState(next.state);

			if (runFinished(game, next)) {
				finishRef.current(next.log, next.state);
				return;
			}
			frame = requestAnimationFrame(tick);
		};

		frame = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frame);
	}, [game, active]);

	const press = useCallback(
		(input: I) => {
			const current = progressRef.current;
			if (current === null || !activeRef.current || runFinished(game, current)) return;
			progressRef.current = queueInput(game, current, input);
		},
		[game],
	);

	// The cast covers the lazy null: the ref is populated during the first
	// render, before any caller can hold this function.
	const peek = useCallback(() => progressRef.current as RunProgress<S, I>, []);

	// `finished` is derived from the rendered state rather than the ref, so it
	// changes when the screen does.
	return {
		state,
		over: state.over,
		finished: state.over || state.step >= game.maxSteps,
		press,
		peek,
	};
}
